import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { keycloakConfig, keycloakInitOptions, appConfig } from '../config/keycloak';
import apiService from '../service/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [keycloak, setKeycloak] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initializedRef = useRef(false); // Prevent multiple initialization

  const logout = useCallback((redirectUri) => {
    if (keycloak) {
      try {
        // Use Keycloak's standard logout with proper redirect
        const logoutRedirectUri = redirectUri || window.location.origin;
        console.log('🚪 Closing session with redirectUri:', logoutRedirectUri);
        keycloak.logout({ redirectUri: logoutRedirectUri });
      } catch (error) {
        console.error('❌ Error during logout:', error);
        // Fallback: try logout without redirectUri
        try {
          keycloak.logout();
        } catch (fallbackError) {
          console.error('❌ Fallback logout also failed:', fallbackError);
        }
      }
    }
  }, [keycloak]);

  useEffect(() => {
    const initKeycloak = async () => {
      // Prevent multiple initialization - CRITICAL for avoiding infinite loops
      if (initializedRef.current) {
        console.log('🔒 Keycloak ya fue inicializado, saltando re-inicialización');
        return;
      }

      // Additional check: if we're in the middle of OAuth callback (hash contains state/code)
      // let Keycloak process it naturally without interruption
      const hash = window.location.hash;
      if (hash && (hash.includes('state=') || hash.includes('code='))) {
        console.log('🔄 OAuth callback detectado en hash, procesando...');
      }

      try {        
        // Store the original path before authentication (excluding auth routes and main-menu)
        const currentPath = window.location.pathname;
        if (!currentPath.startsWith('/auth') && currentPath !== '/main-menu') {
          // Store in sessionStorage for post-login redirect
          sessionStorage.setItem('keycloak_original_path', currentPath);
          console.log('📍 Ruta original guardada:', currentPath);
        } else if (currentPath === '/main-menu') {
          // Clear any previous path when on main-menu to avoid redirecting away
          sessionStorage.removeItem('keycloak_original_path');
        }
        
        // Dynamically import Keycloak to avoid errors if the package is not installed
        const Keycloak = (await import('keycloak-js')).default;
        const kc = new Keycloak(keycloakConfig);

        // Configure initialization options
        const initOptions = {
          ...keycloakInitOptions,
          // Use current path or main-menu as redirect URI
          redirectUri: appConfig.appUrl + (currentPath !== '/auth/login' ? currentPath : '/main-menu'),
          // Ensure that the client uses the standard OAuth2 flow
          flow: 'standard',
          // Disable iframe check to avoid cross-origin problems
          checkLoginIframe: false,
          // Configure longer timeout
          silentCheckSsoFallback: true
        };
 
        const auth = await kc.init(initOptions);
        
        setKeycloak(kc);
        setAuthenticated(auth);
        initializedRef.current = true; // Mark as initialized

        // Register external token provider for apiService (Keycloak)
        apiService.setTokenProvider(() => kc?.token || '');

        if (auth) {
          try {
            const userInfo = await kc.loadUserInfo();
            setUser(userInfo);
          } catch (userError) {
            console.warn('Error loading user information:', userError);
          }
        
          // Clean URL to remove query params AND hash fragments without reloading the page
          // This is critical to prevent infinite loops with OAuth state parameters
          if (window.history.replaceState) {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            console.log('✅ URL limpiada exitosamente (query params y hash removidos)');
          }
        
          // INTELLIGENT REDIRECTION: Check if we should redirect to original path
          const storedPath = sessionStorage.getItem('keycloak_original_path');
          if (storedPath && window.location.pathname !== storedPath) {
            // Clear the stored path
            sessionStorage.removeItem('keycloak_original_path');
            // Redirect to the original requested path
            window.location.replace(window.location.origin + storedPath);
          }
        } else {
          console.log('❌ Usuario no autenticado, redirigiendo a login...');
          // If not authenticated and not on the login page, redirect to Keycloak
          // EXCEPTION: Don't redirect for public external routes
          if (!currentPath.startsWith('/auth') && !currentPath.startsWith('/external/application') && !currentPath.startsWith('/offers')) {
            // Store the current path for post-login redirect
            sessionStorage.setItem('keycloak_original_path', currentPath);
            // Use the native Keycloak method for login with local URL
            kc.login({
              redirectUri: appConfig.appUrl + currentPath
            });
          }
        }
        
        // Configure automatic token refresh
        kc.onTokenExpired = () => {
          console.log('🔄 Token expired, refreshing...');
          kc.updateToken(70).then((refreshed) => {
            if (refreshed) {
              console.log('✅ Token automatically refreshed');
            }
          }).catch(() => {
            console.error('❌ Error refreshing token');
            logout();
          });
        };

      } catch (error) {
        console.error('❌ Error initializing Keycloak:', error);
        setError(error);
        
        // If there is a timeout or initialization error, try direct login
        if (error.message && error.message.includes('Timeout')) {
          console.log('⏰ Timeout detected, redirecting to login...');
          // Redirect directly to Keycloak login with local URL
          const loginUrl = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/auth?client_id=${keycloakConfig.clientId}&redirect_uri=${encodeURIComponent(appConfig.appUrl + '/main-menu')}&response_type=code`;
          window.location.href = loginUrl;
        }
      } finally {
        setLoading(false);
      }
    };

    initKeycloak();
  }, []); // Empty dependencies - should only run ONCE on mount

  const login = useCallback(() => {
    if (keycloak && initializedRef.current) {
      console.log('🔐 Iniciando login con Keycloak...');
      // Use native Keycloak login with proper redirect URI (local)
      const currentPath = window.location.pathname;
      const redirectUri = appConfig.appUrl + (currentPath !== '/auth/login' ? currentPath : '/main-menu');
      keycloak.login({ redirectUri });
    } else if (!keycloak || !initializedRef.current) {
      console.log('🔐 Keycloak no disponible o no inicializado, redirigiendo directamente...');
      // Si Keycloak no está disponible o no inicializado, redirigir directamente
      const loginUrl = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/auth?client_id=${keycloakConfig.clientId}&redirect_uri=${encodeURIComponent(appConfig.appUrl + '/main-menu')}&response_type=code`;
      window.location.href = loginUrl;
    }
  }, [keycloak]);

  const getToken = () => {
    return keycloak?.token;
  };

  const getTokenParsed = () => {
    return keycloak?.tokenParsed;
  };

  const getRealmRoles = () => {
    const roles = keycloak?.tokenParsed?.realm_access?.roles || [];
    return Array.isArray(roles) ? roles : [];
  };

  const getResourceRoles = (clientId) => {
    if (!clientId) return [];
    const resourceAccess = keycloak?.tokenParsed?.resource_access || {};
    const client = resourceAccess[clientId];
    const roles = client?.roles || [];
    return Array.isArray(roles) ? roles : [];
  };

  const hasRole = (role) => {
    return keycloak?.hasRealmRole?.(role) || false;
  };

  const hasResourceRole = (role, resource) => {
    return keycloak?.hasResourceRole?.(role, resource) || false;
  };

  const isAdmin = () => {
    try {
      return hasRole('ROLE_ADMIN');
    } catch (error) {
      console.warn('Error checking admin role:', error);
      return false;
    }
  };

  const isUser = () => {
    try {
      return hasRole('ROLE_USER');
    } catch (error) {
      console.warn('Error checking user role:', error);
      return false;
    }
  };

  const value = {
    keycloak,
    authenticated,
    user,
    loading,
    error,
    login,
    logout,
    getToken,
    getTokenParsed,
    getRealmRoles,
    getResourceRoles,
    hasRole,
    hasResourceRole,
    isAdmin,
    isUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 